import ConfirmationEventBus from 'primevue/confirmationeventbus'
import { reactive } from 'vue'

import { getI18n } from '@/app/i18n'
import { useAuthStore } from '@/app/stores/auth'
import { closeOverlay, openOverlay } from '@/shared/ui/overlay-stack'

export type ConfirmVariant = 'warning' | 'danger' | 'info' | 'success'

export interface ConfirmOptions {
  title: string
  text?: string
  /**
   * Lines shown as a list under the text (05: the members a change affects). Plain text — legacy built an HTML list
   * into the SweetAlert body (I18N.md rule 4).
   */
  items?: string[]
  /** Default: "Confirm" (confirm) / "Close" (alert). */
  okText?: string
  /** Default: "Cancel". */
  cancelText?: string
  variant?: ConfirmVariant
}

/** The embedded option of `confirmWithOption()` (legacy `SweetAlertConfirmOption`: a switch inside the dialog). */
export interface ConfirmOption {
  label: string
  /** Initial state (bank "Delete associated transactions" starts on). */
  checked?: boolean
  /** `switch` (legacy default) or `checkbox`. */
  kind?: 'switch' | 'checkbox'
}

/** Extra content `<AppFeedbackHost>` renders inside the dialog (list, option). */
export interface ConfirmKitContent {
  items: string[]
  option: { label: string; checked: boolean; kind: 'switch' | 'checkbox' } | null
}

const ICONS: Record<ConfirmVariant, string> = {
  warning: 'pi pi-exclamation-triangle',
  danger: 'pi pi-exclamation-circle',
  info: 'pi pi-info-circle',
  success: 'pi pi-check-circle',
}

const labels = {
  confirm: () => getI18n().global.t('common.action.confirm'),
  cancel: () => getI18n().global.t('common.action.cancel'),
  close: () => getI18n().global.t('common.action.close'),
  dontRemind: () => getI18n().global.t('common.confirm.dontRemind'),
}

function show(options: ConfirmOptions, kit: ConfirmKitContent, oneButton: boolean): Promise<boolean> {
  const id = Symbol('confirm')
  return new Promise((resolve) => {
    let settled = false
    const settle = (value: boolean) => {
      if (settled) return
      settled = true
      closeOverlay(id)
      resolve(value)
    }
    // Escape = cancel, as SweetAlert2 did — handled by the overlay stack so a confirm over a modal closes alone.
    openOverlay(id, () => {
      settle(false)
      ConfirmationEventBus.emit('close')
    })
    ConfirmationEventBus.emit('confirm', {
      header: options.title,
      message: options.text ?? '',
      icon: ICONS[options.variant ?? (oneButton ? 'success' : 'warning')],
      acceptLabel: options.okText ?? (oneButton ? labels.close() : labels.confirm()),
      rejectLabel: options.cancelText ?? labels.cancel(),
      rejectProps: oneButton ? { style: { display: 'none' } } : { severity: 'secondary', outlined: true },
      acceptProps: { severity: options.variant === 'danger' && !oneButton ? 'danger' : 'primary' },
      defaultFocus: 'accept',
      closeOnEscape: false,
      kit,
      accept: () => settle(true),
      reject: () => settle(false),
      onHide: () => settle(false),
    })
  })
}

const plain = (options: ConfirmOptions): ConfirmKitContent => ({ items: options.items ?? [], option: null })

/**
 * Asks a question (legacy SweetAlert2 confirm). Resolves `true` on OK, `false` on cancel, Escape or close. Rendered by
 * `<AppFeedbackHost>`; usable outside components.
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return show(options, plain(options), false)
}

/**
 * A confirm with one embedded option (11: "Delete associated transactions" when deleting bank accounts — legacy
 * `SweetAlertConfirmOption`). Resolves with the answer and the option's final state.
 */
export async function confirmWithOption(
  options: ConfirmOptions & { option: ConfirmOption },
): Promise<{ confirmed: boolean; checked: boolean }> {
  const kit = reactive<ConfirmKitContent>({
    items: options.items ?? [],
    option: {
      label: options.option.label,
      checked: options.option.checked ?? false,
      kind: options.option.kind ?? 'switch',
    },
  })
  const confirmed = await show(options, kit, false)
  return { confirmed, checked: kit.option?.checked ?? false }
}

/**
 * A confirm with "Don't remind me again" (08/11 "You won't be able to delete an invoice after recording a payment!"):
 * once the user confirmed with the box ticked, later calls with the same `name` resolve `true` at once. The flag is
 * per viewer, kept with the session preferences (legacy `dontRemind<name>`, `useDontRemind`).
 */
export async function confirmDismissible(name: string, options: ConfirmOptions): Promise<boolean> {
  const auth = useAuthStore()
  if (auth.isDontRemind(name)) return true
  const kit = reactive<ConfirmKitContent>({
    items: options.items ?? [],
    option: { label: labels.dontRemind(), checked: false, kind: 'checkbox' },
  })
  const confirmed = await show(options, kit, false)
  if (confirmed && kit.option?.checked) auth.setDontRemind(name, true)
  return confirmed
}

/**
 * A message with one button (legacy SweetAlert2 success/info alert — "Deleted!" after a delete, 04/07/08/10/11).
 * Resolves when it is closed.
 */
export async function alert(options: Omit<ConfirmOptions, 'cancelText'>): Promise<void> {
  await show(options, plain(options), true)
}

export function useConfirm(): {
  confirm: typeof confirm
  confirmWithOption: typeof confirmWithOption
  confirmDismissible: typeof confirmDismissible
  alert: typeof alert
} {
  return { confirm, confirmWithOption, confirmDismissible, alert }
}
